import queryClient from "api/queryClient.ts";
import { formStructureApi } from "api/TenantSlug/FormStructure/api.ts";
import { outgoingPostbackApi } from "api/TenantSlug/OutgoingPostback/api.ts";
import {
    useGetOutgoingPostbackQuery,
    useUpdateOutgoingPostbackMutation,
} from "api/TenantSlug/OutgoingPostback/hooks.ts";
import { DiagramWithStorage } from "components/CC/Diagrams/DiagramWithStorage/DiagramWithStorage.tsx";
import { CCEntityType } from "components/CC/types/entityConfig.ts";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData";
import { useCallback } from "react";
import { PermissionEnum } from "types/enums.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { mergeScenarioFormFields } from "utils/scenarioFormStructure";
import { getTrackingToolToItems } from "utils/tenantHeaderItems.ts";

export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/tracking/_layout/tracking-api/_layout/$id/_layout/editor/"
)({
    component: TrackingToolToEditorPage,
    staticData: {
        permissions: [PermissionEnum.ToolTrackingOutgoingEditEditor],
        showInSideMenu: false,
        label: "pages.tool.tracking.to.editor.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.tracking.to.editor.title"),
        };
    },
});

function TrackingToolToEditorPage() {
    const { id } = Route.useParams();

    const {
        data: freshData,
        isLoading,
        isError,
    } = useGetOutgoingPostbackQuery(id, { filter: { include: "scenarios,formStructures" } }, { staleTime: 0 });

    const outgoingPostback = freshData?.outgoingPostback;
    const scenarioId = outgoingPostback?.scenarios?.[0]?.id ?? null;

    const formStructureIds = outgoingPostback?.form_structures?.map((fs) => fs.id).filter(Boolean) ?? [];

    const scenarioFormStructuresQuery = useFetchAllPaginatedData(
        (payload) => formStructureApi.index(payload),
        {
            filter: {
                filter: { id: { in: formStructureIds } },
                include: "formFields",
            },
        },
        "formStructures",
        { enabled: formStructureIds.length > 0 }
    );

    const mergedFormFields = mergeScenarioFormFields(scenarioFormStructuresQuery.data ?? []);

    const updatePostbackMutation = useUpdateOutgoingPostbackMutation(id);

    const onScenarioCreated = useCallback(
        async (newScenarioId: string): Promise<void> => {
            // Validate entity still exists before linking the scenario
            const freshData = await queryClient.fetchQuery({
                queryKey: ["getOutgoingPostback", id, "scenarioLink"],
                queryFn: () => outgoingPostbackApi.show(id, {}),
                staleTime: 0,
            });

            const postback = freshData.outgoingPostback;
            if (!postback) {
                throw new Error("Failed to link scenario: outgoing postback payload is missing.");
            }

            return new Promise((resolve, reject) => {
                updatePostbackMutation.mutate(
                    { scenario_ids: { 0: newScenarioId } },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({
                                queryKey: ["getOutgoingPostback", id],
                            });
                            resolve();
                        },
                        onError: reject,
                    }
                );
            });
        },
        [id, updatePostbackMutation]
    );

    if (!outgoingPostback) {
        return (
            <QueryState
                isLoading={isLoading}
                isError={isError || !isLoading}
                errorKey="errors.loading_outgoing_postback"
            />
        );
    }

    return (
        <TenantPage headerNavigationItems={() => getTrackingToolToItems()} isEdit>
            <SectionContentWrapper withBackground>
                <DiagramWithStorage
                    key={id}
                    entityId={outgoingPostback.id}
                    entityType={CCEntityType.TrackingOut}
                    scenarioId={scenarioId}
                    onScenarioCreated={onScenarioCreated}
                    formFields={mergedFormFields}
                />
            </SectionContentWrapper>
        </TenantPage>
    );
}
